import {
  ChevronLeft,
  ChevronRight,
  Trash2,
  Image as ImageIcon,
  Video,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Spinner } from "@/components/ui";
import { useStorageObjectUrl } from "@/hooks/useStorageObjectUrl";
import type { MediaItem } from "@/types";

interface MainViewerProps {
  media: MediaItem;
  currentIndex: number;
  totalCount: number;
  editable: boolean;
  onPrev: () => void;
  onNext: () => void;
  onRemove: () => void;
}

function LoadingImage({
  src,
  alt,
  loadingLabel,
  failed,
}: {
  src: string | undefined;
  alt: string;
  loadingLabel: string;
  failed?: boolean;
}) {
  if (failed) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-neutral-800">
        <ImageIcon className="h-12 w-12 text-surface-500" aria-hidden />
      </div>
    );
  }

  if (!src) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-neutral-800">
        <Spinner size="lg" variant="onDark" loadingLabel={loadingLabel} />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className="h-full w-full object-cover"
    />
  );
}

export function MainViewer({
  media,
  currentIndex,
  totalCount,
  editable,
  onPrev,
  onNext,
  onRemove,
}: MainViewerProps) {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("activities");
  const showNavigation = totalCount > 1;
  const storage = useStorageObjectUrl(media.url);
  const resolvedUrl =
    storage.status === "ready" ? storage.url : undefined;
  const storageFailed = storage.status === "error";

  return (
    <div className="relative overflow-hidden rounded-xl bg-neutral-900">
      <div className="relative aspect-video">
        {media.type === "image" ? (
          <LoadingImage
            src={resolvedUrl}
            alt={`Media ${currentIndex + 1}`}
            loadingLabel={tCommon("loading")}
            failed={storageFailed}
          />
        ) : storageFailed ? (
          <div className="absolute inset-0 flex items-center justify-center bg-neutral-800">
            <Video className="h-12 w-12 text-surface-500" aria-hidden />
          </div>
        ) : (
          <video
            src={resolvedUrl}
            className="h-full w-full object-cover"
            controls
            preload="metadata"
          />
        )}

        {/* Navigation */}
        {showNavigation && (
          <>
            <button
              type="button"
              onClick={onPrev}
              className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-surface-0/90 p-2 shadow-lg transition-all hover:scale-110 hover:bg-surface-0"
              title={t("media.prevTitle")}
            >
              <ChevronLeft className="h-5 w-5 text-surface-700" />
            </button>
            <button
              type="button"
              onClick={onNext}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-surface-0/90 p-2 shadow-lg transition-all hover:scale-110 hover:bg-surface-0"
              title={t("media.nextTitle")}
            >
              <ChevronRight className="h-5 w-5 text-surface-700" />
            </button>
          </>
        )}

        {/* Media counter badge */}
        <div className="absolute left-3 top-3">
          <span className="inline-flex items-center gap-1 rounded-full bg-black/50 px-2.5 py-1 text-xs font-medium text-white backdrop-blur-sm">
            {media.type === "image" ? (
              <ImageIcon className="h-3 w-3" />
            ) : (
              <Video className="h-3 w-3" />
            )}
            {currentIndex + 1} / {totalCount}
          </span>
        </div>

        {/* Delete button */}
        {editable ? (
          <button
            type="button"
            onClick={onRemove}
            className="absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-surface-0 px-3 py-1.5 text-xs font-semibold text-error-600 shadow-lg ring-1 ring-black/10 transition-colors hover:bg-error-fill hover:text-white"
            title={tCommon("delete")}
          >
            <Trash2 className="h-3.5 w-3.5" />
            <span>{tCommon("delete")}</span>
          </button>
        ) : null}
      </div>
    </div>
  );
}
